import EventHelper from "../../../../common/network/helpers/event/EventHelper";
import { EventEditionDetails, IProduct, OrganisationList, IPersonBadge } from "../../../../common/types";
import { useDispatch, useSelector, useStore } from "react-redux";
import AuthHelper from "../../../../common/auth/helpers/AuthHelper";
import {
  MAX_MULTILINGUAL_ITEMS,
  ODIN_EVENT_EDITION_STORAGE_KEY,
  ODIN_PRODUCTS_FETCH_INTERVAL,
  SNACKBAR_VARIANTS
} from "../../../../common/constants";
import { useLanguageSwitch } from "../../../header/menu/components/languageSwitch/hooks/useLanguageSwitch";
import PublicApiService from "../../../../services/PublicApiService";
import MagentoHelper from "../../../../common/network/helpers/magento/MagentoHelper";
import productsUtils from "../../../../common/utils/productsUtils";
import React, { useEffect, useRef, useState } from "react";
import BadgeHelper from "../../../../common/network/helpers/badges/BadgeHelper";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useEventConfiguration } from "../../../eventSelectPage/hooks/useConfiguration/useEventConfiguration";
import { useBadgeUpdate } from "../../../search/details/form/hooks/useBadgeUpdate";
import { filteringOrganisationsByLocale } from "../../filteringOrganisationsByLocale/filteringOrganisationsByLocale";
import { RootState } from "../../../../redux/store";
import PrintService from "../../../../services/PrintService";
import PrintSettingsCache from "../../../printSettings/cache/PrintSettingsCache";
import { PrintMethods } from "../../../../common/enums";
import { setColocatedRegProducts, setRegProducts, setStore } from "../../../../redux/magento/slice/magentoSlice";
import { setOrganisationsList } from "../../../../redux/organisations/slice/organisationSlice";
import {
  resetEventEditionReducer,
  setBadgeLocation,
  setCmsEventDetails,
  setEventEditionBadges,
  setEventEditionSecret
} from "../../../../redux/eventEdition/slice/eventEditionSlice";
import { badgeSyncRequestStarted, badgeSyncRequestCompleted, badgeSyncRequestFailed } from "redux/metadata/slice/metadataSlice";
import { useBadgeLogosRetrieval } from "../useBadgeLogosRetrieval/useBadgeLogosRetrieval";
import { ProductTypeName } from "../../../createBadge/form/CreateBadgeForm.enums";

export const useInitialAppSetup = () => {
  const dispatch = useDispatch();
  const { getState } = useStore();
  const reduxStore = getState() as RootState;
  const { configureInitialLocale } = useLanguageSwitch();
  const { runBadgeLogosRetrieval } = useBadgeLogosRetrieval();
  const [isDataFetching, setIsDataFetching] = useState(true);
  const { t } = useTranslation("common");
  const { enqueueSnackbar } = useSnackbar();
  const eventEditionConfiguration = useEventConfiguration();
  const { getUpdatedBadges } = useBadgeUpdate();
  const reduxState = useSelector((state: RootState) => state);
  const eventEdition = reduxState.eventEdition?.eventEdition;
  const isAdmin = reduxState.metadata.isAdmin;
  const [storageEventEditionId, setStorageEventEditionId] = useState<string | null>(localStorage.getItem(ODIN_EVENT_EDITION_STORAGE_KEY));
  const initializedEventEditionId = useRef<string | null>(null);
  const { error, setError } = eventEditionConfiguration;
  const productsInterval = useRef<NodeJS.Timeout>();

  const initBadgeLocation = async (eventEdition: EventEditionDetails) => {
    const badgeLocation = await EventHelper.getBadgeLocation(eventEdition);
    EventHelper.eventLocation = badgeLocation.fullResponse?.siteDetails?.address?.country;
    dispatch(setCmsEventDetails(badgeLocation.fullResponse));
    dispatch(setBadgeLocation(badgeLocation.url));
  };

  const getEventSecret = async (eventEditionGbsCode: string) => {
    const secretResponse = await PublicApiService.getShowSecret(eventEditionGbsCode);
    dispatch(setEventEditionSecret(secretResponse.secretKey));
    if (!secretResponse?.secretKey) {
      enqueueSnackbar(
        <div className="twolines">
          <b>{t("common:errors.requestError")}</b>
          {t("common:errors.refreshThePage")}
        </div>,
        { ...SNACKBAR_VARIANTS.error, autoHideDuration: 20000 }
      );
    }
    if (navigator && navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ scrt: secretResponse.secretKey });
    }
  };

  const configureStore = async (eventEditionData: EventEditionDetails) => {
    const store = MagentoHelper.getStoreHeader(eventEditionData, eventEditionData.primaryLocale!.toLowerCase());
    dispatch(setStore(store));
    return store;
  };

  const setupBadgeLogos = async (products: IProduct[], currentEventEdition: EventEditionDetails) => {
    if (process.env.REACT_APP_ENVIRONMENT !== "localAgainstShow") {
      await runBadgeLogosRetrieval(products, currentEventEdition);
    }
  };

  const fetchProducts = async (store: string, eventEdition: EventEditionDetails) => {
    if (!reduxStore.metadata.offline) {
      const products = await MagentoHelper.fetchRegProducts(eventEdition, store);
      MagentoHelper.magentoProducts = products;
      const filteredProducts = productsUtils.filterRegProducts(products, eventEdition.gbsCode);
      MagentoHelper.groupedProducts = MagentoHelper.getGroupedProducts(products, eventEdition.gbsCode);
      dispatch(setRegProducts(filteredProducts));
      setIsDataFetching(false);

      return filteredProducts;
    }

    return [];
  };

  const updateProductsListWithColocatedShows = async (store: string, eventEdition: EventEditionDetails) => {
    if (!reduxStore.metadata.offline) {
      const colocatedProducts: IProduct[] = [];
      const colocatedShows = eventEdition?.colocatedShows ?? [];
      for (const colocatedShow of colocatedShows) {
        try {
          const colocatedEventEdition = await EventHelper.fetchEventEdition(colocatedShow.eventEditionId);
          const products = await MagentoHelper.fetchRegProducts(colocatedEventEdition, store);
          colocatedProducts.push(...products);
        } catch (e) {
          console.error(e);
        }
      }
      const bundleColocatedProducts = colocatedProducts.filter(product => product.__typename === ProductTypeName.BUNDLE_PRODUCT);
      dispatch(setColocatedRegProducts(bundleColocatedProducts));
      setIsDataFetching(false);
      return colocatedProducts;
    }
    return [] as IProduct[];
  };

  const mergeBadgesById = (badges: IPersonBadge[]) => {
    const map = new Map<string, IPersonBadge>();
    badges.forEach(badge => {
      if (badge?.id) {
        map.set(badge.id, badge);
      }
    });
    return Array.from(map.values());
  };

  const fetchBadges = async (gbsCode: string) => {
    enqueueSnackbar(<b>{t("common.badgesAreBeingLoaded")}</b>, SNACKBAR_VARIANTS.info);
    const badges = await BadgeHelper.fetchEventBadges(gbsCode);
    dispatch(setEventEditionBadges(mergeBadgesById(badges)));
    await getUpdatedBadges(35);
    enqueueSnackbar(<b>{t("common.badgesAreLoaded")}</b>, SNACKBAR_VARIANTS.success);
  };

  const startProductsInterval = (store: string, info: EventEditionDetails) => {
    if (productsInterval.current) {
      clearInterval(productsInterval.current);
    }

    productsInterval.current = setInterval(async () => {
      await fetchProducts(store, info);
      await updateProductsListWithColocatedShows(store, info);
    }, ODIN_PRODUCTS_FETCH_INTERVAL);
  };

  const initAppData = async (info: EventEditionDetails) => {
    configureInitialLocale();
    initializedEventEditionId.current = info.id;

    try {
      if (AuthHelper.isWithoutAuth()) {
        return;
      }
      dispatch(badgeSyncRequestStarted());
      await initBadgeLocation(info);
      await getEventSecret(info.gbsCode);
      const store = await configureStore(info);
      const products = await fetchProducts(store, info);
      await updateProductsListWithColocatedShows(store, info);

      await setupBadgeLogos(products, info);
      await fetchBadges(info.gbsCode);

      startProductsInterval(store, info);
      dispatch(badgeSyncRequestCompleted());
    } catch (e) {
      dispatch(badgeSyncRequestFailed());
      dispatch(resetEventEditionReducer());
      eventEditionConfiguration.setError((e as Error).message ?? t("errors.clientError"));
      localStorage.removeItem(ODIN_EVENT_EDITION_STORAGE_KEY);
      setStorageEventEditionId("");
    }
  };

  const fetchOrganisations = async () => {
    try {
      const organisations = Array<OrganisationList>();
      for (let counter = MAX_MULTILINGUAL_ITEMS; counter === MAX_MULTILINGUAL_ITEMS; ) {
        const {
          data: { results: exhibitingOrganisations }
        } = await PublicApiService.getExhibitingOrganisations(eventEdition!.id, MAX_MULTILINGUAL_ITEMS, organisations.length);
        organisations.push(...exhibitingOrganisations);
        counter = exhibitingOrganisations.length;
      }
      const result = filteringOrganisationsByLocale(organisations, eventEdition!.primaryLocale!);
      dispatch(setOrganisationsList(result));
    } catch (e) {
      enqueueSnackbar(<b>{t("common:errors.exhibitorRegistrationUnavailable")}</b>, SNACKBAR_VARIANTS.error);
    }
  };

  useEffect(() => {
    if (isAdmin && eventEdition && initializedEventEditionId.current !== eventEdition.id) {
      setIsDataFetching(true);
      initAppData(eventEdition);
      fetchOrganisations();
      return;
    }
    if (eventEdition) {
      setIsDataFetching(false);
    }
  }, [isAdmin, eventEdition]);

  useEffect(() => {
    eventEditionConfiguration.fetchCounties();

    if (!AuthHelper.isWithoutAuth()) {
      if (storageEventEditionId) {
        eventEditionConfiguration.fetchEventEdition(storageEventEditionId);
      }
      const cachedPrintSettings = PrintSettingsCache.get();
      if (cachedPrintSettings.printMethod === PrintMethods.PRINT_SERVER) {
        PrintService.setupPdfWS({ useCache: true }).catch(console.error);
      }
    }
  }, []);

  useEffect(() => {
    if (!eventEdition && storageEventEditionId) {
      dispatch(resetEventEditionReducer());
      localStorage.removeItem(ODIN_EVENT_EDITION_STORAGE_KEY);
      setStorageEventEditionId(null);
    }
  }, []);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(<b>{error}</b>, SNACKBAR_VARIANTS.error);
      setError("");
    }
  }, [error]);

  useEffect(() => {
    return () => {
      clearInterval(productsInterval.current!);
    };
  }, []);

  return { storageEventEditionId, eventEditionConfiguration, isDataFetching };
};
